import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link as RouterLink } from 'react-router-dom';
import dayjs from 'dayjs';
import { blogApi, blogKeys } from '../api/blog.api';
import type { BlogPost } from '../types';

const FALLBACK_COVER =
  'https://images.unsplash.com/photo-1488646953015-11dddf093fd1?auto=format&fit=crop&w=1200&q=80';

function formatDate(post: BlogPost) {
  return dayjs(post.publishedAt || post.createdAt).format('D MMM YYYY');
}

export function BlogListPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: blogKeys.list({ page: 1, limit: 24 }),
    queryFn: () => blogApi.listPublished({ page: 1, limit: 24 }),
  });

  const posts = data?.data ?? [];
  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <Box sx={{ width: '100%', overflowX: 'clip', bgcolor: '#F8FAFC', minHeight: '70vh' }}>
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 260, sm: 320, md: 420 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: 'url("/images/blog-page.png")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: { xs: 'scroll', md: 'fixed' },
          color: '#FFFFFF',
          textAlign: 'center',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(15, 23, 42, 0.55)',
          },
        }}
      >
        <Container
          maxWidth="md"
          sx={{ position: 'relative', zIndex: 1, py: { xs: 5, md: 6 }, px: { xs: 2.5, sm: 3 } }}
        >
          <Typography
            component="h1"
            sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
              fontWeight: 700,
              mb: { xs: 1.5, md: 3 },
              color: '#FFFFFF',
              textShadow: '0 2px 12px rgba(0,0,0,0.35)',
              lineHeight: 1.2,
            }}
          >
            Blogs
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '0.9375rem', sm: '1.125rem', md: '1.5rem' },
              fontWeight: 400,
              lineHeight: 1.6,
              maxWidth: 700,
              mx: 'auto',
              color: 'rgba(255,255,255,0.92)',
              textShadow: '0 1px 6px rgba(0,0,0,0.3)',
            }}
          >
            Travel stories, local tips, and guides from the trplin team
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 }, px: { xs: 2, sm: 3 } }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {isError && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            Failed to load blogs. Please try again later.
          </Alert>
        )}

        {!isLoading && !isError && posts.length === 0 && (
          <Box sx={{ py: 8, maxWidth: 480 }}>
            <Typography
              variant="h5"
              fontWeight={700}
              gutterBottom
              sx={{ fontFamily: '"Playfair Display", Georgia, serif', color: 'primary.dark' }}
            >
              No posts yet
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
              Check back soon for travel stories and destination guides.
            </Typography>
          </Box>
        )}

        {featured && (
          <Box sx={{ mb: { xs: 5, md: 7 } }}>
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'primary.main',
                mb: 2,
              }}
            >
              Latest story
            </Typography>

            <Card
              elevation={0}
              sx={{
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                bgcolor: 'background.paper',
                boxShadow: '0 8px 30px rgba(15, 23, 42, 0.06)',
              }}
            >
              <CardActionArea
                component={RouterLink}
                to={`/blogs/${featured.slug}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1.15fr 0.85fr' },
                  alignItems: 'stretch',
                }}
              >
                <CardMedia
                  component="img"
                  image={featured.coverImage || FALLBACK_COVER}
                  alt={featured.title}
                  sx={{
                    width: '100%',
                    height: { xs: 240, sm: 300, md: '100%' },
                    minHeight: { md: 360 },
                    objectFit: 'cover',
                  }}
                />
                <CardContent
                  sx={{
                    p: { xs: 3, md: 4.5 },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    textAlign: 'left',
                  }}
                >
                  <Chip
                    label={formatDate(featured)}
                    size="small"
                    sx={{
                      alignSelf: 'flex-start',
                      mb: 2,
                      bgcolor: 'primary.50',
                      color: 'primary.dark',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                    }}
                  />
                  <Typography
                    component="h2"
                    sx={{
                      fontFamily: '"Playfair Display", Georgia, serif',
                      fontWeight: 700,
                      fontSize: { xs: '1.5rem', md: '2rem' },
                      lineHeight: 1.25,
                      color: 'text.primary',
                      mb: 1.5,
                    }}
                  >
                    {featured.title}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.75,
                      mb: 2.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {featured.excerpt}
                  </Typography>
                  <Typography
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      color: 'primary.main',
                      fontWeight: 700,
                      fontSize: '0.9375rem',
                    }}
                  >
                    Read story <ArrowForwardIcon sx={{ fontSize: 18 }} />
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Box>
        )}

        {rest.length > 0 && (
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 2,
                mb: 3,
                pb: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Playfair Display", Georgia, serif',
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', md: '1.75rem' },
                  color: 'primary.dark',
                }}
              >
                More stories
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {posts.length} {posts.length === 1 ? 'post' : 'posts'}
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 2.5, md: 3 }}>
              {rest.map((post) => (
                <Grid key={post.id} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      overflow: 'hidden',
                      bgcolor: 'background.paper',
                      transition: 'transform 200ms ease, box-shadow 200ms ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 16px 40px -12px rgba(15, 23, 42, 0.12)',
                      },
                    }}
                  >
                    <CardActionArea
                      component={RouterLink}
                      to={`/blogs/${post.slug}`}
                      sx={{
                        height: '100%',
                        alignItems: 'stretch',
                        display: 'flex',
                        flexDirection: 'column',
                        textAlign: 'left',
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="200"
                        image={post.coverImage || FALLBACK_COVER}
                        alt={post.title}
                      />
                      <CardContent sx={{ flex: 1, p: 2.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'primary.main',
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                          }}
                        >
                          {formatDate(post)}
                        </Typography>
                        <Typography
                          component="h3"
                          sx={{
                            mt: 1,
                            mb: 1.25,
                            fontWeight: 700,
                            fontFamily: '"Playfair Display", Georgia, serif',
                            fontSize: '1.1875rem',
                            lineHeight: 1.3,
                            color: 'text.primary',
                          }}
                        >
                          {post.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.65,
                          }}
                        >
                          {post.excerpt}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>
    </Box>
  );
}
